// ==================== GURUKUL — Course completions (REQUIREMENTS_REGISTER LMS-15/-16, manual phase) ====================
// The LMS Team's (or an OPD/admin's) daily upload of learner course completions: date, learner_id, course_id,
// completed/in_progress. Same page for the 'lms' and 'admin' views — the API decides what each role may see/do.
import { showToast, showConfirm } from '../components/toasts.js';

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}
const fmtDateTime = d => d ? new Date(d).toLocaleString() : '—';
const fmtRange = u => {
  if (!u.date_range_start) return '—';
  return u.date_range_start === u.date_range_end ? u.date_range_start : `${u.date_range_start} – ${u.date_range_end}`;
};

export function getLmsCompletionsPage() {
  return `<h1 class="page-title">Course completions</h1>
    <div class="card">
      <h2>Upload today's file</h2>
      <p class="page-intro">A CSV with 4 columns, in this order: <code>date, learner_id, course_id, status</code> — one row per
        learner per course. A header row is optional — with or without one is fine. <code>status</code> is <code>completed</code>
        or <code>in_progress</code>. <code>date</code> is the day the LMS recorded that status (YYYY-MM-DD) — if the file covers
        more than one day (e.g. a Monday including the weekend), that's fine, just include each row's own date.
        <code>learner_id</code> and <code>course_id</code> must be Gurukul's own codes (the same ones already used for account
        requests and the catalog), not the LMS's internal IDs.</p>
      <p class="page-intro"><small>Example row: <code>2026-09-27,L100013,NG-SAT-101-A000,completed</code></small></p>
      <div class="form-group"><input type="file" id="gkCompFile" accept=".csv,text/csv"></div>
      <button type="button" id="gkCompUpload">Upload</button>
      <div id="gkCompResult" style="margin-top:14px;"></div>
    </div>
    <div class="card">
      <h2>Past uploads</h2>
      <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:end;margin-bottom:12px;">
        <div class="form-group" style="margin:0;"><label for="gkCompFrom">Covers from</label><input type="date" id="gkCompFrom" style="max-width:160px;"></div>
        <div class="form-group" style="margin:0;"><label for="gkCompTo">Covers to</label><input type="date" id="gkCompTo" style="max-width:160px;"></div>
        <div class="form-group" style="margin:0;"><label for="gkCompUploader">Uploaded by</label><select id="gkCompUploader" style="max-width:200px;"><option value="">Everyone</option></select></div>
        <div class="form-group" style="margin:0;flex:1;min-width:180px;"><label for="gkCompSearch">Search</label><input type="text" id="gkCompSearch" placeholder="Date, file name, or uploader" style="width:100%;"></div>
        <button type="button" id="gkCompClearFilters">Clear</button>
      </div>
      <div id="gkCompUploads"><p class="empty-state">Loading…</p></div>
    </div>`;
}

function errorsTableHtml(errors) {
  return `<div class="table-container"><table class="table-stack-mobile"><thead><tr><th>Row</th><th>Learner</th><th>Course</th><th>Date</th><th>Reason</th></tr></thead><tbody>
    ${errors.map(er => `<tr><td data-label="Row">${er.row}</td><td data-label="Learner">${esc(er.learner_code || '—')}</td>
      <td data-label="Course">${esc(er.course_code || '—')}</td><td data-label="Date">${esc(er.date || '—')}</td><td data-label="Reason">${esc(er.reason)}</td></tr>`).join('')}
    </tbody></table></div>`;
}

function renderResult(box, u) {
  const hasErrors = u.error_rows > 0;
  box.innerHTML = `
    <div style="padding:10px 12px;border:1px solid var(--border);border-radius:6px;">
      <strong>Uploaded for ${esc(fmtRange(u))}</strong><br>
      ${u.matched_rows} matched · ${u.duplicate_rows} unchanged · ${u.error_rows} error(s) — ${u.total_rows} row(s) total
      ${hasErrors ? `<br><button type="button" id="gkCompShowErrors" style="margin-top:6px;">Show errors</button>
        <div id="gkCompErrors" style="display:none;margin-top:8px;">${errorsTableHtml(u.errors)}</div>` : ''}
    </div>`;
  if (hasErrors) {
    box.querySelector('#gkCompShowErrors').addEventListener('click', (e) => {
      const out = box.querySelector('#gkCompErrors');
      const show = out.style.display === 'none';
      out.style.display = show ? '' : 'none';
      e.target.textContent = show ? 'Hide errors' : 'Show errors';
    });
  }
}

function currentFilters() {
  return {
    date_from: document.getElementById('gkCompFrom').value,
    date_to: document.getElementById('gkCompTo').value,
    uploaded_by: document.getElementById('gkCompUploader').value,
    q: document.getElementById('gkCompSearch').value.trim(),
  };
}

// Every past upload with errors is clickable: expands a detail row below it, fetching the full error list on
// first click (the list view only carries counts, not the errors themselves) and caching it after that.
async function loadUploads() {
  const box = document.getElementById('gkCompUploads');
  const f = currentFilters();
  const params = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
  let d;
  try { d = await api('/api/gurukul/lms-completions' + (params.toString() ? '?' + params.toString() : '')); }
  catch (err) { box.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; return; }

  const uploaderSel = document.getElementById('gkCompUploader');
  const keepValue = uploaderSel.value;
  uploaderSel.innerHTML = '<option value="">Everyone</option>' + d.uploaders.map(u => `<option value="${u.id}">${esc(u.name)}</option>`).join('');
  uploaderSel.value = keepValue;

  if (!d.uploads.length) { box.innerHTML = '<p class="page-intro">No uploads match.</p>'; return; }
  box.innerHTML = `<div class="table-container"><table class="table-stack-mobile"><thead>
      <tr><th>Uploaded</th><th>By</th><th>File</th><th>Covers (dates in file)</th><th>Matched</th><th>Unchanged</th><th>Errors</th><th></th></tr></thead><tbody>
      ${d.uploads.map(u => {
        const errClickable = u.error_rows > 0;
        return `<tr class="${errClickable ? 'row-clickable gkCompRow' : ''}" data-id="${u.id}"><td data-label="Uploaded">${fmtDateTime(u.created_at)}</td>
          <td data-label="By">${esc(u.uploaded_by_name || '—')}</td><td data-label="File">${esc(u.filename || '—')}</td>
          <td data-label="Covers">${esc(fmtRange(u))}</td><td data-label="Matched">${u.matched_rows}</td><td data-label="Unchanged">${u.duplicate_rows}</td>
          <td data-label="Errors">${errClickable ? `<span class="badge badge-danger">${u.error_rows}</span> <small>(click for detail)</small>` : '0'}</td>
          <td data-label="" class="action-buttons">${u.can_archive ? `<button type="button" class="gkCompArchive" data-id="${u.id}" data-name="${esc(u.filename || 'this upload')}">Archive</button>` : ''}</td></tr>
          ${errClickable ? `<tr class="gkCompDetailRow" data-for="${u.id}" style="display:none;"><td colspan="8"></td></tr>` : ''}`;
      }).join('')}
      </tbody></table></div>`;

  box.querySelectorAll('.gkCompRow').forEach(row => row.addEventListener('click', async (e) => {
    if (e.target.closest('.gkCompArchive')) return;
    const id = row.dataset.id;
    const detailRow = box.querySelector(`.gkCompDetailRow[data-for="${id}"]`);
    const cell = detailRow.querySelector('td');
    const show = detailRow.style.display === 'none';
    detailRow.style.display = show ? '' : 'none';
    if (show && !cell.dataset.built) {
      cell.innerHTML = '<p class="empty-state">Loading…</p>';
      try {
        const { upload } = await api('/api/gurukul/lms-completions?id=' + encodeURIComponent(id));
        cell.dataset.built = '1';
        cell.innerHTML = errorsTableHtml(upload.errors);
      } catch (err) { cell.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; }
    }
  }));
  box.querySelectorAll('.gkCompArchive').forEach(btn => btn.addEventListener('click', async (e) => {
    e.stopPropagation();
    if (!(await showConfirm(`Archive <strong>${esc(btn.dataset.name)}</strong>? It will no longer show in this list, but the completion data it wrote is kept.`))) return;
    try {
      await api('/api/gurukul/lms-completions', { method: 'POST', body: JSON.stringify({ action: 'archive', id: btn.dataset.id }) });
      showToast('Archived', 'success');
      loadUploads();
    } catch (err) { showToast(err.message, 'error'); }
  }));
}

export async function initLmsCompletionsPage() {
  document.getElementById('gkCompUpload').addEventListener('click', async () => {
    const input = document.getElementById('gkCompFile');
    const file = input.files?.[0];
    if (!file) { showToast('Choose a CSV file first', 'error'); return; }
    if (!(await showConfirm(`Upload <strong>${esc(file.name)}</strong>?`))) return;
    try {
      const csvText = await file.text();
      const { upload } = await api('/api/gurukul/lms-completions', { method: 'POST', body: JSON.stringify({ filename: file.name, csv_text: csvText }) });
      renderResult(document.getElementById('gkCompResult'), upload);
      showToast(`Uploaded — ${upload.matched_rows} matched, ${upload.error_rows} error(s)`, upload.error_rows ? 'warning' : 'success');
      input.value = '';
      loadUploads();
    } catch (err) { showToast(err.message, 'error'); }
  });
  ['gkCompFrom', 'gkCompTo', 'gkCompUploader'].forEach(id => document.getElementById(id).addEventListener('change', loadUploads));
  let searchTimer;
  document.getElementById('gkCompSearch').addEventListener('input', () => { clearTimeout(searchTimer); searchTimer = setTimeout(loadUploads, 300); });
  document.getElementById('gkCompClearFilters').addEventListener('click', () => {
    document.getElementById('gkCompFrom').value = '';
    document.getElementById('gkCompTo').value = '';
    document.getElementById('gkCompUploader').value = '';
    document.getElementById('gkCompSearch').value = '';
    loadUploads();
  });
  await loadUploads();
}
